import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { supabase } from '../lib/supabaseClient'

// ============================================================
// login.js
//
// WHAT THIS PAGE DOES
// Handles both signing in and signing up from a single form.
// The user toggles between the two modes with a link at the bottom.
//
// SIGN-UP NOTE
// We call supabase.auth.signUp() directly here instead of using
// useAuth's signUp function. We need access to data.user.id right
// away so we can write a row to the performers table immediately.
// useAuth's listener only fires after email confirmation, which
// would be too late. After sign-up succeeds, we show a "check
// your email" screen instead of navigating anywhere.
// ============================================================

function Login() {

  // ---- FORM MODE STATE ----

  const [isSignUp, setIsSignUp] = useState(false)
  // false = showing the sign-in form
  // true  = showing the sign-up form
  // toggled by the link at the bottom of the card


  // ---- FORM FIELD STATE ----

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [performerName, setPerformerName] = useState('')
  // performerName only shown and required when isSignUp is true


  // ---- STATUS STATE ----

  const [isLoading, setIsLoading] = useState(false)
  // true while the Supabase auth call is in-flight
  // disables the submit button and shows "Loading..."

  const [errorMsg, setErrorMsg] = useState('')
  // local error message from form validation or failed auth call
  // '' means no error

  const [signUpSuccess, setSignUpSuccess] = useState(false)
  // true after a successful sign-up
  // switches the whole card to the "check your email" screen


  // ---- HOOK ----

  const { signIn, error } = useAuth()
  // signIn    = calls Supabase signInWithPassword and updates auth state
  // error     = last error from useAuth (covers sign-in failures)
  // We handle sign-up directly here (see note at top)


  // -------------------------------------------------------
  // handleSubmit
  //
  // Runs when the user submits the form.
  // Branches into sign-up or sign-in based on isSignUp flag.
  // -------------------------------------------------------
  const handleSubmit = async (e) => {
    e.preventDefault()
    // prevent the browser from reloading the page on form submit

    setIsLoading(true)
    setErrorMsg('')
    // clear any previous error before the new attempt

    try {
      if (isSignUp) {

        // ---- SIGN UP FLOW ----

        if (!performerName.trim()) {
          setErrorMsg('Performer name is required')
          setIsLoading(false)
          return
          // stop here before hitting the database with an empty name
        }

        // Call supabase directly so we get data.user.id in the response.
        // We need that ID to upsert into the performers table right away.
        const { data, error: signUpError } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: { performer_name: performerName.trim() },
            // performer_name is stored in user_metadata so it is
            // available without a separate database query
          },
        })

        if (signUpError) {
          setErrorMsg(signUpError.message)
          setIsLoading(false)
          return
        }

        // ---- PERFORMERS TABLE UPSERT ----
        // Create a row in the performers table so managers can find
        // this user by drag_name and so foreign key constraints work.
        // onConflict: 'auth_user_id' makes this safe to run again if
        // the user somehow creates an account twice.
        if (data?.user?.id) {
          const { error: performerError } = await supabase
            .from('performers')
            .upsert(
              {
                drag_name:    performerName.trim(),
                auth_user_id: data.user.id,
                email:        email.trim().toLowerCase(),
              },
              { onConflict: 'auth_user_id' }
              // update the existing row if one already has this auth_user_id
            )

          if (performerError) {
            // This is non-fatal. The auth sign-up succeeded already.
            // Just log it and let the user continue.
            console.error('Login: performers upsert error:', performerError.message)
          }
        }

        setSignUpSuccess(true)
        // switch the card to the "check your email" confirmation screen
        setIsLoading(false)
        return

      } else {

        // ---- SIGN IN FLOW ----
        await signIn(email, password)
        // signIn updates the auth state via onAuthStateChange,
        // which causes App.js to re-render with the authenticated UI.
        // No explicit navigation needed here.
      }

    } catch (err) {
      setErrorMsg(err.message || 'An error occurred')
      console.error('Auth error:', err)
    } finally {
      setIsLoading(false)
      // always re-enable the form whether it worked or not
    }
  }

  const displayError = errorMsg || error
  // show whichever error we have -- local form error takes priority
  // over the useAuth hook error


  // ---- "CHECK YOUR EMAIL" SCREEN ----
  // Shown after a successful sign-up instead of the login form.
  if (signUpSuccess) {
    return (
      <div className="login-container">
        <div className="login-card login-card--centered">
          {/* .success-icon-block: centres the big icon with bottom margin */}
          <div className="success-icon-block">
            <i className="bi bi-envelope-check-fill"></i>
          </div>
          <h1>Check Your Email!</h1>
          <p className="about-prose">
            We sent a confirmation link to <strong>{email}</strong>.
            Click the link in that email to activate your account, then come back here to sign in.
          </p>
          <p className="text-muted" style={{ fontSize: 'var(--text-sm)', marginBottom: 'var(--spacing-lg)' }}>
            Don't see it? Check your spam folder.
          </p>
          <button
            className="btn-primary"
            onClick={() => {
              // Reset everything and go back to the sign-in form
              setSignUpSuccess(false)
              setIsSignUp(false)
              setEmail('')
              setPassword('')
              setPerformerName('')
            }}
          >
            Back to Sign In
          </button>
        </div>
      </div>
    )
  }


  // ---- MAIN LOGIN / SIGN-UP FORM ----

  return (
    <div className="login-container">
      <div className="login-card">

        <h1>Performer Pocket Planner</h1>
        <p>{isSignUp ? 'Join the Party!' : 'Welcome Back!'}</p>

        {/* Error banner -- shown when there is an error from auth or form validation */}
        {displayError && (
          <div className="error-banner">
            {displayError}
          </div>
        )}

        <form onSubmit={handleSubmit}>

          {/* Performer name field -- only visible on the sign-up form */}
          {isSignUp && (
            <div className="form-group">
              <label>Performer Name</label>
              <input
                type="text"
                placeholder="Your stage name"
                value={performerName}
                onChange={(e) => setPerformerName(e.target.value)}
                required
              />
            </div>
          )}

          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              placeholder="you@email.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label>Password</label>
            <input
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
            />
          </div>

          {/* Privacy policy notice -- only shown on sign-up */}
          {isSignUp && (
            <p className="login-privacy">
              By creating an account you agree to our{' '}
              <Link to="/privacy-policy" className="link-primary">
                Privacy Policy
              </Link>
              .{' '}
              We store your gig data, music, photos, and location only to power
              the app features. We never sell your data.
            </p>
          )}

          <button type="submit" disabled={isLoading}>
            {isLoading ? 'Loading...' : isSignUp ? "Let's Go!" : 'Come On In!'}
            {/* isLoading = show spinner text while request runs */}
            {/* isSignUp  = different button label for each mode  */}
          </button>
        </form>

        {/* Toggle link at the bottom to switch between sign-in and sign-up */}
        <p className="login-footer">
          {isSignUp ? 'Already Signed Up? ' : 'New here? '}
          <span onClick={() => {
            setIsSignUp(!isSignUp)
            // flip the mode
            setErrorMsg('')
            // clear any error from the previous mode so it does not
            // show on the other form
          }}>
            {isSignUp ? 'Sign in' : 'Sign up'}
          </span>
        </p>
      </div>
    </div>
  )
}

export default Login